// @ts-nocheck
import React from 'react';
import { flatMap } from 'lodash';
import { useParams } from 'react-router-dom';

import NotFound404 from './components/Common/NotFound404';
import { getLabelPathFromLabelConfig, reactLazyRetry } from './utils/Utils';
import SuspenseComponent from './components/Common/SuspenseComponent';
import ErrorBoundary from './components/Common/ErrorBoundary';
import ArchivedEntity from './views/ModelInventory/ArchivedEntity';
import { DEFAULT_ROUTE_CONFIG } from './const/defaultRoutesConfig';
import ClearCacheLogout from './components/ClearCacheLogout/ClearCacheLogout';
import PluginAccessView from './plugins/PluginAccessView';

const Layout = reactLazyRetry(() => import('./views/Layout/Layout'));
const Login = reactLazyRetry(() => import('./views/Login/Login'));
const Account = reactLazyRetry(() => import('./views/Login/Account'));
const Organization = reactLazyRetry(() => import('./views/Login/Organization'));

const HelpAndSupport = reactLazyRetry(
  () => import('./views/HelpAndSupport/HelpAndSupport')
);
const ThemeAppearance = reactLazyRetry(
  () => import('./views/Configuration/ThemeAppearance/ThemeAppearance')
);
const UserManagement = reactLazyRetry(
  () => import('./views/UserManagement/UserManagement')
);
const AppManagement = reactLazyRetry(
  () => import('./views/AppManagement/AppManagement')
);
const EmailManagement = reactLazyRetry(
  () => import('./views/Configuration/EmailManagement/EmailManagement')
);

const ModelInventory = reactLazyRetry(
  () => import('./views/ModelInventory/ModelInventory')
);
const ModelInventoryCreateModel = reactLazyRetry(
  () => import('./views/ModelInventory/CreateModelStepper')
);
const Dashboard = reactLazyRetry(
  () => import('./views/ModelInventory/Dashboard')
);
const BulkUpload = reactLazyRetry(
  () => import('./views/BulkActions/BulkUpload/BulkUpload')
);
const BulkTransition = reactLazyRetry(
  () => import('./views/BulkActions/BulkTransition/BulkTransition')
);
const BulkUpdate = reactLazyRetry(
  () => import('./views/BulkActions/BulkUpdate/BulkUpdate')
);
const BulkDocumentUpload = reactLazyRetry(
  () => import('./views/BulkActions/BulkDocumentUpload/BulkDocumentUpload')
);
const ModelAssociation = reactLazyRetry(
  () => import('./views/ModelAssociation/ModelAssociation')
);
const DocumentInventory = reactLazyRetry(
  () => import('./views/DocumentInventory/DocumentInventory')
);
const EditModelAssociation = reactLazyRetry(
  () => import('./views/ModelAssociation/EditModelAssociation')
);
const EditTemplate = reactLazyRetry(
  () => import('./views/Configuration/Template/EditTemplateView')
);
const TemplateList = reactLazyRetry(
  () => import('./views/Configuration/Template/TemplateList')
);
const AttributeLibrary = reactLazyRetry(
  () => import('./views/Configuration/AttributeLibrary/AttributeLibrary')
);
const ConstantAttributes = reactLazyRetry(
  () => import('./views/Configuration/ConstantAttributes/ConstantAttributes')
);
const GlobalAttributes = reactLazyRetry(
  () => import('./views/Configuration/GlobalAttributes')
);
const TeamRolesView = reactLazyRetry(
  () => import('./views/Configuration/TeamRoles/TeamRolesView')
);
const EditModelDetails = reactLazyRetry(
  () => import('./views/ModelInventory/EditModelDetails')
);
const WorkFlowList = reactLazyRetry(
  () => import('./views/Configuration/Workflow/WorkFlowList')
);
const EditWorkflow = reactLazyRetry(
  () => import('./views/Configuration/Workflow/EditWorkflow')
);
const GenerateWorkflow = reactLazyRetry(
  () => import('./views/Configuration/Workflow/GenerateWorkflow')
);
const Rules = reactLazyRetry(() => import('./views/RulesAndReports/Rules'));

const Reports = reactLazyRetry(
  () => import('./views/RulesAndReports/Reports.v2')
);
const AdvanceReport = reactLazyRetry(
  () => import('./views/RulesAndReports/AdvanceReports/Main')
);
const ViewAdvanceReport = reactLazyRetry(
  () => import('./views/RulesAndReports/AdvanceReports/ViewReport')
);
const ReportCategory = reactLazyRetry(
  () => import('./views/RulesAndReports/ReportCategory/Category')
);

const ChartVisualization = reactLazyRetry(
  () => import('./views/RulesAndReports/ReportCategory/ChartVisualization')
);

const ReportCanvas = reactLazyRetry(
  () => import('./views/RulesAndReports/ReportCanvas/Canvas')
);

const DocumentTemplate = reactLazyRetry(
  () => import('./views/Configuration/Documentation/DocumentTemplate')
);
const DynamicChart = reactLazyRetry(
  () => import('./views/Configuration/DynamicChart/DynamicChart')
);

const ToolbarList = reactLazyRetry(
  () => import('./views/Configuration/DynamicToolbar/ToolbarList')
);
const CreateToolbarConfig = reactLazyRetry(
  () => import('./views/Configuration/DynamicToolbar/CreateToolbarConfig')
);
const EditToolbarConfig = reactLazyRetry(
  () => import('./views/Configuration/DynamicToolbar/EditToolbarConfig')
);
const AutomationList = reactLazyRetry(
  () => import('./views/Configuration/Automation/AutomationList')
);
const CreateAutomation = reactLazyRetry(
  () => import('./views/Configuration/Automation/CreateAutomation')
);
const DashboardConfig = reactLazyRetry(
  () => import('./views/Configuration/DashboardConfig/DashboardConfig')
);
const NotAuthorized = reactLazyRetry(
  () => import('./components/Common/NotAuthorized')
);

const ModelRequest = reactLazyRetry(
  () => import('./views/ModelInventory/ModelRequest/ModelRequest')
);
const EditModelRequest = reactLazyRetry(
  () => import('./views/ModelInventory/ModelRequest/EditModelRequest')
);
const AccessRequest = reactLazyRetry(
  () => import('./views/ModelInventory/AccessRequest/AccessRequest')
);

const EditAccessRequest = reactLazyRetry(
  () => import('./views/ModelInventory/AccessRequest/EditAccessRequest')
);

const UseCaseList = reactLazyRetry(
  () => import('./views/ModelInventory/UseCase/UseCaseList')
);
const EditUseCase = reactLazyRetry(
  () => import('./views/ModelInventory/UseCase/EditUseCase')
);
const QueryList = reactLazyRetry(
  () => import('./views/ModelInventory/Query/QueryList')
);
const EditQuery = reactLazyRetry(
  () => import('./views/ModelInventory/Query/EditQuery')
);

const HandleRedirectionLink = reactLazyRetry(
  () =>
    import('./components/Common/HandleRedirectionLink/HandleRedirectionLink')
);

const CreateEmailTemplateView = reactLazyRetry(
  () =>
    import('./views/Configuration/EmailManagement/EmailTemplate/CreateEmailTemplateView')
);

const SystemID = reactLazyRetry(
  () => import('./views/Configuration/Entity/SystemID')
);

const IDViaTemplate = reactLazyRetry(
  () => import('./views/Configuration/Entity/IDViaTemplate')
);

const EntityManagement = reactLazyRetry(
  () => import('./views/Configuration/Entity/EntityManagement')
);

const EditEntityView = reactLazyRetry(
  () => import('./views/Configuration/Entity/EditEntityView')
);

const ConfigurationLogs = reactLazyRetry(
  () => import('./views/Logs/ConfigurationLogs')
);

/**
 * Remounts `EditModelDetails` on `:uniqueId` change so state from the
 * previous model (e.g. model → model navigation) doesn't leak into the
 * next one — fixes skipped skeleton and stale-response races.
 */
const KeyedEditModelDetails = () => {
  const { uniqueId } = useParams();
  return <EditModelDetails key={uniqueId} />;
};

/**
 * Same as {@link KeyedEditModelDetails}, for `EditModelAssociation` —
 * needed for same-type artifact navigation (e.g. sub-process → sub-process).
 */
const KeyedEditModelAssociation = () => {
  const { uniqueId } = useParams();
  return <EditModelAssociation key={uniqueId} />;
};

const Routes = [
  {
    name: 'Model Inventory',
    path: DEFAULT_ROUTE_CONFIG?.modelInventory?.path || 'model',
    key: 'modelInventory',
    element: <ModelInventory />,
    permission: 'ModelInventory_modelInventory',
    children: [
      {
        name: 'Archived Models',
        path: DEFAULT_ROUTE_CONFIG?.archivedModels?.path || 'model/archived',
        key: 'archivedModels',
        element: <ArchivedEntity />,
        permission: 'ModelInventory_modelInventory',
      },
      {
        name: 'Add Model',
        path: DEFAULT_ROUTE_CONFIG?.createModel?.path || 'model/add-model',
        key: 'createModel',
        element: <ModelInventoryCreateModel />,
        permission: 'ModelInventory_modelInventory',
      },
      {
        name: 'Edit Model ',
        key: 'editModel',
        path: DEFAULT_ROUTE_CONFIG?.editModel?.path || 'model/:uniqueId',
        element: <KeyedEditModelDetails />,
        permission: 'ModelInventory_modelInventory',
      },
    ],
  },
  {
    name: 'Home',
    key: 'home',
    path: DEFAULT_ROUTE_CONFIG?.home?.path || 'home',
    element: <Dashboard />,
    permission: 'Home',
  },
  {
    name: 'Bulk Upload',
    key: 'bulkUpload',
    path: DEFAULT_ROUTE_CONFIG?.bulkUpload?.path || 'bulk-upload',
    element: <BulkUpload />,
    permission: 'BulkActions_bulkUpload',
  },
  {
    name: 'Bulk Transition',
    key: 'bulkTransition',
    path: DEFAULT_ROUTE_CONFIG?.bulkTransition?.path || 'bulk-transition',
    element: <BulkTransition />,
    permission: 'BulkActions_bulkTransition',
  },
  {
    name: 'Bulk Update',
    key: 'bulkUpdate',
    path: DEFAULT_ROUTE_CONFIG?.bulkUpdate?.path || 'bulk-update',
    element: <BulkUpdate />,
    permission: 'BulkActions_bulkTransition',
  },
  {
    name: 'Bulk Document Upload',
    key: 'bulkDocumentUpload',
    path: DEFAULT_ROUTE_CONFIG?.bulkDocumentUpload?.path || 'bulk-document',
    element: <BulkDocumentUpload />,
    permission: 'BulkActions_bulkDocumentUpload',
  },
  {
    key: 'ModelAssociation',
    path: DEFAULT_ROUTE_CONFIG?.ModelAssociation?.path || 'model-artifact',
    element: <ModelAssociation />,
    permission: 'ModelAssociation',
    children: [
      {
        name: 'Archived Artifacts',
        path:
          DEFAULT_ROUTE_CONFIG?.archivedArtifacts?.path ||
          'model-artifact/archived',
        key: 'archivedArtifacts',
        element: <ArchivedEntity />,
        permission: 'ModelAssociation',
      },
      {
        key: 'editModelAssociation',
        path:
          DEFAULT_ROUTE_CONFIG?.editModelAssociation?.path ||
          'model-artifact/:uniqueId',
        element: <KeyedEditModelAssociation />,
        permission: 'ModelAssociation',
      },
    ],
  },
  {
    key: 'workflow',
    path: DEFAULT_ROUTE_CONFIG?.workflow?.path || 'workflows',
    element: <WorkFlowList />,
    permission: 'Configuration_workflow',
    children: [
      {
        key: 'editWorkflow',
        path:
          DEFAULT_ROUTE_CONFIG?.editWorkflow?.path || 'workflows/edit-workflow',
        element: <EditWorkflow />,
        permission: 'Configuration_workflow',
      },
      {
        key: 'generateWorkflow',
        path:
          DEFAULT_ROUTE_CONFIG?.generateWorkflow?.path ||
          'workflows/generate-workflow',
        element: <GenerateWorkflow />,
        permission: 'Configuration_workflow',
      },
    ],
  },
  {
    name: 'System ID',
    key: 'systemId',
    path: 'system-id',
    element: <SystemID />,
    permission: 'Configuration_dynamicId',
  },
  {
    name: 'ID via Template',
    key: 'idViaTemplate',
    path: 'id-via-template',
    element: <IDViaTemplate />,
    permission: 'Configuration_dynamicId',
  },
  {
    name: 'Entity Management',
    key: 'entityManagement',
    path: DEFAULT_ROUTE_CONFIG?.entityManagement?.path || 'entity-management',
    element: <EntityManagement />,
    permission: 'Configuration_entityManagement',
  },
  {
    key: 'templateList',
    path: DEFAULT_ROUTE_CONFIG?.templateList?.path || 'templates',
    element: <TemplateList />,
    permission: 'Configuration_templateList',
    children: [
      {
        key: 'createTemplate',
        path:
          DEFAULT_ROUTE_CONFIG?.createTemplate?.path ||
          'templates/create-template',
        element: <EditTemplate />,
        permission: 'Configuration_templateList',
      },
      {
        key: 'editTemplate',
        path:
          DEFAULT_ROUTE_CONFIG?.editTemplate?.path || 'templates/edit-template',
        element: <EditTemplate />,
        permission: 'Configuration_templateList',
      },
    ],
  },
  {
    key: 'editEntity',
    path:
      DEFAULT_ROUTE_CONFIG?.editEntity?.path ||
      'configuration/entity/:uniqueId',
    element: <EditEntityView />,
    permission: 'Configuration_entityManagement',
  },
  {
    key: 'globalAttributes',
    path: DEFAULT_ROUTE_CONFIG?.globalAttributes?.path || 'global-attributes',
    element: <GlobalAttributes />,
    permission: 'Configuration_globalAttributes',
  },
  {
    key: 'Attributes',
    path: DEFAULT_ROUTE_CONFIG?.attributeLibrary?.path || 'attributes',
    element: <AttributeLibrary />,
    permission: 'Configuration_attributes',
  },
  {
    key: 'ConstantAttributes',
    path:
      DEFAULT_ROUTE_CONFIG?.constantAttributes?.path || 'constant-attributes',
    element: <ConstantAttributes />,
    permission: 'Configuration_constantAttributes',
  },

  {
    key: 'rules',
    path: DEFAULT_ROUTE_CONFIG?.rules?.path || 'rules',
    element: <Rules />,
    permission: 'Configuration_rules',
  },
  {
    key: 'teamRoles',
    path: DEFAULT_ROUTE_CONFIG?.teamRoles?.path || 'team-roles',
    element: <TeamRolesView />,
    permission: 'Configuration_teamRoles',
  },
  {
    key: 'report',
    path: DEFAULT_ROUTE_CONFIG?.report?.path || 'report',
    element: <Reports />,
    permission: 'RulesReports_report',
  },
  {
    key: 'advanceReport',
    path: DEFAULT_ROUTE_CONFIG?.advanceReport?.path || 'advanceReport',
    element: <AdvanceReport />,
    permission: 'RulesReports_advanceReport',
    children: [
      {
        name: 'Report Category',
        key: 'reportCategory',
        path:
          DEFAULT_ROUTE_CONFIG?.reportCategory?.path ||
          'advanceReport/report-category',
        element: <ReportCategory />,
        permission: 'RulesReports_advanceReport',
      },
      {
        name: 'Report Canvas',
        key: 'reportCanvas',
        path:
          DEFAULT_ROUTE_CONFIG?.reportCanvas?.path ||
          'advanceReport/report-canvas',
        element: <ReportCanvas />,
        permission: 'RulesReports_advanceReport',
      },
      {
        name: 'View Report',
        key: 'viewReport',
        path:
          DEFAULT_ROUTE_CONFIG?.viewAdvanceReport?.path ||
          'advanceReport/viewAdvanceReport',
        element: <ViewAdvanceReport />,
        permission: 'RulesReports_advanceReport',
      },
      {
        name: 'Chart Visualization',
        key: 'chartVisualization',
        path: 'advanceReport/chart-visualization',
        element: <ChartVisualization />,
        permission: 'RulesReports_advanceReport',
      },
    ],
  },
  {
    key: 'userManagement',
    path: DEFAULT_ROUTE_CONFIG?.userManagement?.path || 'user-management',
    element: <UserManagement />,
    permission: 'AdministrativeOptions_userManagement',
  },
  {
    key: 'appManagement',
    path: DEFAULT_ROUTE_CONFIG?.appManagement?.path || 'app-management',
    element: <AppManagement />,
    permission: 'AdministrativeOptions_appManagement',
  },
  {
    name: 'Email Management',
    key: 'emailManagement',
    path: DEFAULT_ROUTE_CONFIG?.emailManagement?.path || 'email-management',
    element: <EmailManagement />,
    permission: 'Configuration_emailManagement',
    children: [
      {
        name: 'Create Email Template',
        key: 'createEmailTemplate',
        path:
          DEFAULT_ROUTE_CONFIG?.createEmailTemplate?.path ||
          'email-template/create-email-template',
        element: <CreateEmailTemplateView />,
        permission: 'Configuration_emailManagement',
      },
      {
        name: 'Edit Email Template',
        key: 'editEmailTemplate',
        path:
          DEFAULT_ROUTE_CONFIG?.editEmailTemplate?.path ||
          'email-template/:uniqueId',
        element: <CreateEmailTemplateView />,
        permission: 'Configuration_emailManagement',
      },
    ],
  },
  {
    name: 'Document Inventory',
    key: 'DocumentInventory',
    path: DEFAULT_ROUTE_CONFIG?.documentInventory?.path || 'document-inventory',
    element: <DocumentInventory />,
    permission: 'DocumentInventory_documentInventory',
  },
  {
    name: 'Document Template',
    key: 'DocumentTemplate',
    path: DEFAULT_ROUTE_CONFIG?.documentTemplate?.path || 'document-template',
    element: <DocumentTemplate />,
    permission: 'Configuration_documentTemplate',
  },
  {
    name: 'Dynamic Chart',
    key: 'dynamicChart',
    path: DEFAULT_ROUTE_CONFIG?.dynamicChart?.path || 'dynamic-chart',
    element: <DynamicChart />,
    permission: 'Configuration_dynamicChart',
  },
  {
    key: 'dynamicToolbar',
    path: DEFAULT_ROUTE_CONFIG?.dynamicToolbar?.path || 'dynamic-toolbar',
    element: <ToolbarList />,
    permission: 'Configuration_dynamicToolbar',
    children: [
      {
        name: 'Create Dynamic Toolbar',
        key: 'createDynamicToolbar',
        path: DEFAULT_ROUTE_CONFIG?.createDynamicToolbar?.path,
        element: <CreateToolbarConfig />,
        permission: 'Configuration_dynamicToolbar',
      },
      {
        name: 'Edit Dynamic Toolbar',
        key: 'editDynamicToolbar',
        path: DEFAULT_ROUTE_CONFIG?.editDynamicToolbar?.path,
        element: <EditToolbarConfig />,
        permission: 'Configuration_dynamicToolbar',
      },
    ],
  },
  {
    name: 'Automation',
    key: 'automation',
    path: DEFAULT_ROUTE_CONFIG?.automation?.path || 'automation',
    element: <AutomationList />,
    permission: 'Configuration_automation',
    children: [
      {
        name: 'Create Automation',
        key: 'createAutomation',
        path:
          DEFAULT_ROUTE_CONFIG?.createAutomation?.path ||
          'automation/create-automation',
        element: <CreateAutomation />,
        permission: 'Configuration_automation',
      },
      {
        name: 'Update Automation',
        key: 'updateAutomation',
        path:
          DEFAULT_ROUTE_CONFIG?.updateAutomation?.path ||
          'automation/update-automation',
        element: <CreateAutomation />,
        permission: 'Configuration_automation',
      },
    ],
  },
  {
    name: 'Theme and Appearance',
    key: 'themeAndAppearance',
    path: DEFAULT_ROUTE_CONFIG?.themeAndAppearance?.path || 'theme-appearance',
    element: <ThemeAppearance />,
    permission: 'Configuration_themeAndAppearance',
  },
  {
    name: 'Homepage Setup',
    key: 'homepageSetup',
    path: DEFAULT_ROUTE_CONFIG?.homepageSetup?.path || 'homepage-setup',
    element: <DashboardConfig />,
    permission: 'Configuration_homepageSetup',
  },
  {
    name: ' NotAuthorized',
    key: 'notAuthorized',
    path: 'not-authorized',
    element: <NotAuthorized />,
  },
  {
    name: 'Assessment',
    path: DEFAULT_ROUTE_CONFIG?.modelRequest?.path || 'assessment',
    key: 'modelRequest',
    element: <ModelRequest />,
    permission: 'ModelInventory_modelRequest',
    children: [
      {
        name: 'Edit Assessment',
        key: 'editModelRequest',
        path:
          DEFAULT_ROUTE_CONFIG?.editModelRequest?.path ||
          'assessment/:uniqueId',
        element: <EditModelRequest />,
        permission: 'ModelInventory_modelRequest',
      },
      {
        name: 'Archived Assessments',
        key: 'archivedassessments',
        path:
          DEFAULT_ROUTE_CONFIG?.archivedModelRequest?.path ||
          'assessment/archived',
        element: <ArchivedEntity />,
        permission: 'ModelInventory_modelRequest',
      },
    ],
  },
  {
    name: 'Access Request',
    path: DEFAULT_ROUTE_CONFIG?.accessRequest?.path || 'accessRequest',
    key: 'accessRequest',
    element: <AccessRequest />,
    // permission: 'ModelInventory_accessRequest',
    children: [
      {
        name: 'Access Request',
        key: 'editAccessRequest',
        path:
          DEFAULT_ROUTE_CONFIG?.editAccessRequest?.path ||
          'accessRequest/:uniqueId',
        element: <EditAccessRequest />,
      },
    ],
  },
  {
    name: 'UseCase',
    path: DEFAULT_ROUTE_CONFIG?.useCase?.path || 'useCase',
    key: 'useCase',
    element: <UseCaseList />,
    permission: 'ModelInventory_useCase',
    children: [
      {
        name: 'Edit useCase',
        key: 'editUseCase',
        path: DEFAULT_ROUTE_CONFIG?.editUseCase?.path || 'useCase/:uniqueId',
        element: <EditUseCase />,
        permission: 'ModelInventory_useCase',
      },
      {
        name: 'Archived useCase',
        key: 'archiveUseCase',
        path: DEFAULT_ROUTE_CONFIG?.archivedUseCase?.path || 'useCase/archived',
        element: <ArchivedEntity />,
        permission: 'ModelInventory_useCase',
      },
    ],
  },
  {
    name: 'Query',
    path: DEFAULT_ROUTE_CONFIG?.query?.path || 'query',
    key: 'query',
    element: <QueryList />,
    permission: 'ModelInventory_query',
    children: [
      {
        name: 'Edit Query',
        key: 'editQuery',
        path: DEFAULT_ROUTE_CONFIG?.editQuery?.path || 'query/:uniqueId',
        element: <EditQuery />,
        permission: 'ModelInventory_query',
      },
    ],
  },
  {
    name: 'Handle Redirection Link',
    key: 'handleRedirectionLink',
    path: 'handle-redirection-link',
    element: <HandleRedirectionLink />,
    // No permission required as this is for external redirections
  },
  {
    name: 'ConfigurationLogs',
    key: 'configurationLogs',
    path: DEFAULT_ROUTE_CONFIG?.configurationLogs?.path || 'system-logs',
    element: <ConfigurationLogs />,
    permission: 'Logs_configurationLogs',
  },
  // {
  //   name: 'InventoryLogs',
  //   key: 'inventoryLogs',
  //   path: DEFAULT_ROUTE_CONFIG?.inventoryLogs?.path || 'inventory-logs',
  //   element: <ConfigurationLogs />,
  //   permission: 'Logs_inventoryLogs',
  // },
];

/**
 * @type {import('react-router-dom').RouteObject[]}
 * */
export const publicRoutes = [
  {
    path: '/login',
    element: <Login />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/login/:accountId',
    element: <Login />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/account',
    element: <Account />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/org',
    element: <Organization />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/clear-client-storage',
    element: <ClearCacheLogout />,
    errorElement: <ErrorBoundary />,
  },

  {
    path: '*',
    element: <NotFound404 />,
    errorElement: <ErrorBoundary />,
  },
];

/**
 * @type {import('react-router-dom').RouteObject[]}
 */
export const pluginRoutes = [
  {
    path: '/uam-requests',
    element: (
      <PluginAccessView
        pluginKey="HDFCUAM.views.UAMRequestListView"
        permissions={['HDFCUAM_UAMMaker', 'HDFCUAM_UAMChecker']}
      />
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/uam-requests/:uniqueId',
    element: (
      <PluginAccessView
        pluginKey="HDFCUAM.views.UAMRequestApprovalView"
        permissions={['HDFCUAM_UAMMaker', 'HDFCUAM_UAMChecker']}
      />
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/uam-reports',
    element: (
      <PluginAccessView pluginKey="HDFCUAM.views.UAMReportsDownloadView" />
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/role-requests',
    element: (
      <PluginAccessView
        pluginKey="HDFCUAM.views.RoleRequestsListView"
        permissions={['HDFCUAM_RoleMaker', 'HDFCUAM_RoleChecker']}
      />
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/role-requests/new',
    element: (
      <PluginAccessView
        pluginKey="HDFCUAM.views.RoleRequestCreateView"
        permissions={['HDFCUAM_RoleMaker']}
      />
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/role-requests/:requestId',
    element: (
      <PluginAccessView
        pluginKey="HDFCUAM.views.RoleRequestApproverView"
        permissions={['HDFCUAM_RoleMaker', 'HDFCUAM_RoleChecker']}
      />
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/uam-parameters',
    element: (
      <PluginAccessView
        pluginKey="HDFCUAM.views.UAMParametersView"
        permissions={['HDFCUAM_RoleMaker', 'HDFCUAM_RoleChecker']}
      />
    ),
    errorElement: <ErrorBoundary />,
  },
];

const mapRoutes = (routes, userPermissions, isNoGroupUser, parentKey = '') =>
  routes.map(({ path, element, permission, key, children }) => {
    const fullKey = parentKey ? `${parentKey}.${key}` : `MENUS.${key}`;
    const routePath = getLabelPathFromLabelConfig(fullKey, 'path', path);

    const hasNoAccess =
      isNoGroupUser || userPermissions?.[permission] === 'None';
    const mappedRoute = {
      path: routePath || path,
      element: hasNoAccess ? (
        <NotAuthorized />
      ) : (
        <SuspenseComponent>{element}</SuspenseComponent>
      ),
      errorElement: <ErrorBoundary />,
    };
    if (children?.length) {
      mappedRoute.children = mapRoutes(
        children,
        userPermissions,
        isNoGroupUser,
        fullKey
      );
    }

    return mappedRoute;
  });

const flattenRoutes = (routes) =>
  flatMap(routes, (route) => {
    const { children, ...rest } = route;
    return children ? [rest, ...flattenRoutes(children)] : rest;
  });

/**
 * @param {Record<string, 'None' | 'View' | 'Manage'>} userPermissions User Permissions returned from API
 * @returns {import('react-router-dom').RouteObject[]}
 */
export const getProtectedRoutes = (userPermissions) => {
  /**
   * if user is not present in any group then he will receive only superadmin bool,
   * i.e object with length 1, logic will break if BE response change
   */
  const isNoGroupUser = Object.keys(userPermissions || {}).length === 1;
  return [
    {
      path: '/',
      element: <Layout />,
      children: [
        ...flattenRoutes(mapRoutes(Routes, userPermissions, isNoGroupUser)),
        ...pluginRoutes,
      ],
    },
    {
      path: '/help',
      element: (
        <SuspenseComponent>
          <HelpAndSupport />
        </SuspenseComponent>
      ),
      errorElement: <ErrorBoundary />,
    },
  ];
};
